import React from 'react';

type UseVoidConfirmParams = {
    clearCart: () => void;
    focusHome: () => void;
};

export type VoidConfirmState = {
    isOpen: boolean;
    open: () => void;
    /** Wired to the alert dialog's controlled `isOpen`/`onOpenChange` — every closing route (Cancel, Esc, the close trigger) lands here. */
    onOpenChange: (open: boolean) => void;
    confirm: () => void;
    /** The dialog's Confirm button, focused once the dialog opens. */
    confirmRef: React.RefObject<HTMLButtonElement | null>;
};

/**
 * The void-confirm dialog's own open/closed state and focus (bos-seller#57),
 * kept out of `VoidConfirmDialog.tsx` so the dialog stays render-only and
 * this can be tested without rendering it. Only `confirm` clears the cart;
 * every other closing route leaves it untouched.
 *
 * Confirm gets initial focus so `Enter` confirms as soon as the dialog
 * opens. Plain `autoFocus` loses this race: `useDialog`'s own "focus the
 * dialog unless a child already grabbed it" effect (`react-aria`) runs in
 * the same effect flush and wins. A `setTimeout` deferred to the next
 * macrotask — after that flush — reliably wins instead.
 *
 * Focus goes home after either route closes it — the same
 * wasOpen-ref-plus-effect shape `ProductPanel` uses for the variant picker,
 * so it fires no matter which route closed the dialog.
 */
export const useVoidConfirm = ({
    clearCart,
    focusHome,
}: UseVoidConfirmParams): VoidConfirmState => {
    const [isOpen, setIsOpen] = React.useState(false);
    const confirmRef = React.useRef<HTMLButtonElement>(null);

    const open = React.useCallback(() => setIsOpen(true), []);
    const onOpenChange = React.useCallback(
        (nextOpen: boolean) => setIsOpen(nextOpen),
        [],
    );
    const confirm = React.useCallback(() => {
        clearCart();
        setIsOpen(false);
    }, [clearCart]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        const timeout = setTimeout(() => confirmRef.current?.focus(), 0);
        return () => clearTimeout(timeout);
    }, [isOpen]);

    const wasOpen = React.useRef(isOpen);
    React.useEffect(() => {
        if (wasOpen.current && !isOpen) {
            focusHome();
        }
        wasOpen.current = isOpen;
    }, [isOpen, focusHome]);

    return { isOpen, open, onOpenChange, confirm, confirmRef };
};
